import type { EditorThemeClasses } from "lexical";

const codeHighlight: NonNullable<EditorThemeClasses["codeHighlight"]> = {
  atrule: "text-teal",
  attr: "text-teal",
  boolean: "text-lc-blocked",
  builtin: "text-plum",
  cdata: "text-neutral-600",
  char: "text-lc-active",
  class: "text-teal",
  "class-name": "text-teal",
  comment: "text-neutral-500 italic",
  constant: "text-lc-blocked",
  deleted: "text-lc-blocked",
  doctype: "text-neutral-600",
  entity: "text-orange-600",
  function: "text-teal",
  important: "text-lc-blocked font-bold",
  inserted: "text-lc-active",
  keyword: "text-plum",
  namespace: "text-teal",
  number: "text-lc-blocked",
  operator: "text-neutral-700",
  prolog: "text-neutral-600",
  property: "text-teal",
  punctuation: "text-neutral-700",
  regex: "text-orange-600",
  selector: "text-lc-active",
  string: "text-lc-active",
  symbol: "text-lc-blocked",
  tag: "text-teal",
  url: "text-teal underline",
  variable: "text-orange-600",
};

const text: NonNullable<EditorThemeClasses["text"]> = {
  bold: "font-bold",
  italic: "italic",
  strikethrough: "line-through",
  underline: "underline",
  underlineStrikethrough: "underline line-through",
  code: "bg-muted px-1.5 py-0.5 rounded text-sm font-mono",
};

export const richTextEditorTheme: EditorThemeClasses = {
  paragraph: "mb-2 last:mb-0",
  quote:
    "border-l-4 border-muted-foreground/30 pl-4 py-2 my-4 italic text-muted-foreground",
  heading: {
    h1: "text-3xl font-bold mb-4 mt-6 first:mt-0",
    h2: "text-2xl font-bold mb-3 mt-5 first:mt-0",
    h3: "text-xl font-semibold mb-2 mt-4 first:mt-0",
    h4: "text-lg font-semibold mb-2 mt-3 first:mt-0",
    h5: "text-base font-semibold mb-1 mt-2 first:mt-0",
  },
  list: {
    nested: {
      listitem: "list-none",
    },
    ol: "list-decimal list-inside my-2 ml-4",
    ul: "list-disc list-inside my-2 ml-4",
    listitem: "mb-1",
  },
  text,
  code: "bg-muted p-4 rounded-lg font-mono text-sm my-4 overflow-x-auto block",
  codeHighlight,
};

/**
 * Typography that mirrors the TipTap "notion-like" document editor (see the
 * heading / paragraph / list scss in `notion-like-editor`): a 16px body at 1.6
 * line-height and an em-based 1.5 / 1.25 / 1.125 / 1.0 heading scale, so a
 * markdown file rendered here reads identically to a file-record doc.
 *
 * Pair it with `applyProseStyles={false}` (so Tailwind Typography's `prose-sm`
 * 14px scale doesn't fight it) and a 16px base on the editor container — the
 * heading and spacing values below are `em`-relative to that base.
 */
export const documentEditorTheme: EditorThemeClasses = {
  paragraph: "mt-5 first:mt-0 leading-[1.6]",
  quote:
    "border-l-4 border-muted-foreground/30 pl-4 my-6 italic text-muted-foreground",
  heading: {
    h1: "text-[1.5em] font-bold leading-tight mt-12 first:mt-0",
    h2: "text-[1.25em] font-bold leading-tight mt-10 first:mt-0",
    h3: "text-[1.125em] font-semibold leading-snug mt-8 first:mt-0",
    h4: "text-[1em] font-semibold mt-8 first:mt-0",
    h5: "text-[0.875em] font-semibold mt-6 first:mt-0",
  },
  list: {
    nested: {
      listitem: "list-none",
    },
    ol: "list-decimal my-6 pl-6",
    ul: "list-disc my-6 pl-6",
    listitem: "leading-[1.6] mt-2 first:mt-0",
  },
  text,
  code: "bg-muted p-4 rounded-lg font-mono text-sm my-4 overflow-x-auto block",
  codeHighlight,
};
